import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { logout } from "../api/auth.js";
import { breachChecker } from "../lib/breach.js";
import { secretClipboard } from "./clipboard.js";
import { useSession } from "./session.js";

export type LockReason = "manual" | "idle";

/** Wipes keys in the worker, a pending clipboard secret and cached plaintext, ends the server session, and returns to login. */
export function useLockVault(): (reason?: LockReason) => Promise<void> {
  const { state, clear } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useCallback(
    async (reason: LockReason = "manual"): Promise<void> => {
      await secretClipboard.flush();
      if (state.status !== "locked") {
        const client = state.client;
        try {
          await client.lock();
        } catch {
          // The worker may already be gone; termination below still drops it.
        }
        client.terminate();
        try {
          await logout();
        } catch {
          // Local lock matters most; the server session expires on its own.
        }
      }
      queryClient.clear();
      breachChecker.clear();
      clear();
      navigate("/login", { replace: true, state: { reason } });
    },
    [state, clear, queryClient, navigate]
  );
}
